<div class="nx-page">

  <!-- ═══════════════ LOADING ═══════════════ -->
  <div *ngIf="isLoading" class="nx-loader">
    <div class="nx-loader-core">
      <div class="nx-loader-ring"></div>
      <div class="nx-loader-ring r2"></div>
      <img src="assets/images/Logo.svg" alt="Nishwik" class="nx-loader-logo" />
    </div>
  </div>

  <!-- ═══════════════ MAIN ═══════════════ -->
  <div *ngIf="!isLoading" class="nx-hero">

    <!-- Ambient animated background -->
    <div class="nx-orbs" aria-hidden="true"><i></i><i></i><i></i></div>
    <div class="nx-gridlines" aria-hidden="true"></div>

    <div class="nx-container">

      <header class="nx-brand">
        <img src="assets/images/Logo.svg" alt="Nishwik Logo" />
        <span class="nx-secure"><i class="nx-live"></i> Secure onboarding</span>
      </header>

      <div class="nx-grid">

        <!-- Left: welcome + reasons to love it (desktop only) -->
        <aside class="nx-intro">
          <h6><span class="nx-spark"></span>Welcome to {{ entityName }}</h6>
          <h2>Your onboarding,<br />done in <span class="nx-grad">under a minute.</span></h2>
          <p>
            Choose your role, verify your mobile number and hit submit.
            {{ entityName }} reviews your request, and you're ready to go.
          </p>

          <ul class="nx-points">
            <li *ngFor="let h of highlights; let i = index" [style.--i]="i">
              <span class="nx-point-icon"><i class="bi" [ngClass]="h.icon"></i></span>
              <div>
                <strong>{{ h.title }}</strong>
                <span>{{ h.text }}</span>
              </div>
            </li>
          </ul>

          <div class="nx-intro-chips">
            <span class="nx-chip-info" *ngIf="entityAddress"><i class="bi bi-geo-alt-fill"></i>{{ entityAddress }}</span>
            <span class="nx-chip-info" *ngIf="entityContact"><i class="bi bi-telephone-fill"></i>{{ entityContact }}</span>
            <span class="nx-chip-info" *ngIf="entityEmail"><i class="bi bi-envelope-fill"></i>{{ entityEmail }}</span>
          </div>
        </aside>

        <!-- Right: the card -->
        <main class="nx-card">

          <!-- Progress stepper -->
          <ol class="nx-steps" aria-label="Onboarding progress">
            <li *ngFor="let label of stepLabels; let i = index"
                [class.done]="isStepDone(i)"
                [class.active]="i === stepIndex && currentStep !== 'success'">
              <span class="nx-step-dot">
                <i *ngIf="isStepDone(i)" class="bi bi-check-lg"></i>
                <b *ngIf="!isStepDone(i)">{{ i + 1 }}</b>
              </span>
              <span class="nx-step-name">{{ label }}</span>
            </li>
          </ol>

          <!-- Entity strip -->
          <div class="nx-entity" *ngIf="currentStep !== 'success'">
            <div class="nx-entity-logo">
              <img *ngIf="entityLogo; else entityInitial" [src]="entityLogo" [alt]="entityName" />
              <ng-template #entityInitial><span>{{ entityName.charAt(0) }}</span></ng-template>
            </div>
            <div class="nx-entity-meta">
              <strong>{{ entityName }}</strong>
              <span>{{ moduleIcon }} {{ module | titlecase }} self-onboarding</span>
            </div>
            <span class="nx-entity-live"><i class="nx-live"></i> Open</span>
          </div>

          <!-- ═══ STEP 1 · ROLE ═══ -->
          <section *ngIf="currentStep === 'role'" class="nx-step" @stepAnim>
            <h2 class="nx-title">Who's joining us today?</h2>
            <p class="nx-sub">Pick the role that fits you best. We'll tailor the rest.</p>

            <div class="nx-roles">
              <button
                type="button"
                *ngFor="let role of roles; let i = index"
                class="nx-role"
                [style.--i]="i"
                [class.selected]="selectedRole === role.value"
                (click)="selectRole(role.value)"
                (mousemove)="spot($event)"
                [id]="'role-' + role.value">
                <span class="nx-role-icon">{{ role.icon }}</span>
                <span class="nx-role-text">
                  <span class="nx-role-label">{{ role.label }}</span>
                  <span class="nx-role-desc">{{ role.desc }}</span>
                </span>
                <i class="bi nx-role-arrow" [ngClass]="selectedRole === role.value ? 'bi-check-circle-fill' : 'bi-arrow-right-circle'"></i>
              </button>
            </div>
          </section>

          <!-- ═══ STEP 2 · FORM (OTP inline) ═══ -->
          <section *ngIf="currentStep === 'form'" class="nx-step" @stepAnim>

            <div class="nx-formhead">
              <button type="button" class="nx-back" (click)="goBack()" aria-label="Change role">
                <i class="bi bi-arrow-left"></i>
              </button>
              <div>
                <h2 class="nx-title">{{ isStudentAdmission ? 'Student admission' : 'Tell us about you' }}</h2>
                <span class="nx-rolechip">{{ roleIcon }} {{ roleLabel }}</span>
              </div>
            </div>

            <!-- Live completion bar -->
            <div class="nx-progress" [class.full]="formProgress === 100"
                 role="progressbar" aria-valuemin="0" aria-valuemax="100" [attr.aria-valuenow]="formProgress">
              <div class="nx-progress-track"><span [style.width.%]="formProgress"></span></div>
              <small>{{ formProgress === 100 ? 'All set. Ready to submit!' : formProgress + '% complete' }}</small>
            </div>

            <form class="nx-form" (ngSubmit)="submitOnboarding()" autocomplete="off" novalidate>

              <!-- Society · Building (all society roles, on top) + Flat (flat member only) -->
              <div class="nx-row" *ngIf="module === 'society'" [class.single]="!isFlatMember">
                <div class="nx-field">
                  <label class="nx-label" for="inp-buildingId">
                    Building / Wing
                    <i class="req" *ngIf="isFlatMember">*</i>
                    <small *ngIf="!isFlatMember">optional</small>
                    <span *ngIf="buildingsLoading" class="nx-spin dark ms-1"></span>
                  </label>
                  <select id="inp-buildingId" class="nx-input" name="buildingId"
                    [ngModel]="form.buildingId" (ngModelChange)="onBuildingChange($event)">
                    <option [ngValue]="0">Select Building</option>
                    <option *ngFor="let b of buildings" [ngValue]="b.buildingId">
                      {{ b.buildingName }}
                    </option>
                  </select>
                </div>
                <div class="nx-field" *ngIf="isFlatMember">
                  <label class="nx-label" for="inp-flatId">
                    Flat <i class="req">*</i>
                    <span *ngIf="flatsLoading" class="nx-spin dark ms-1"></span>
                  </label>
                  <select id="inp-flatId" class="nx-input" name="flatId"
                    [(ngModel)]="form.flatId" [disabled]="!form.buildingId">
                    <option [ngValue]="0">{{ form.buildingId ? 'Select Flat' : 'Select Building First' }}</option>
                    <option *ngFor="let f of flats" [ngValue]="f.flatId">
                      {{ f.flatNumber || f.flatNo }}
                    </option>
                  </select>
                </div>
              </div>

              <!-- Student: class chips -->
              <div class="nx-field" *ngIf="isStudentAdmission">
                <label class="nx-label">Which class are you applying for? <i class="req">*</i></label>
                <div class="nx-chips" *ngIf="classesLoading">
                  <span class="nx-skel" *ngFor="let s of [1,2,3,4]"></span>
                </div>
                <div class="nx-chips" *ngIf="!classesLoading && classes.length">
                  <button
                    type="button"
                    class="nx-pick"
                    *ngFor="let cls of classes; let i = index"
                    [style.--i]="i"
                    [class.on]="form.classId === cls.classId"
                    (click)="pickClass(cls.classId)"
                    [id]="'class-' + cls.classId">
                    {{ cls.className }}
                  </button>
                </div>
                <small class="nx-note" *ngIf="!classesLoading && !classes.length">
                  No classes are open for admission at this school yet.
                </small>
              </div>

              <!-- Names: first / middle (optional) / last -->
              <div class="nx-row nx-names">
                <div class="nx-field nx-f-first">
                  <label class="nx-label" for="inp-firstName">
                    {{ isStudentAdmission ? 'Student first name' : 'First name' }} <i class="req">*</i>
                  </label>
                  <input id="inp-firstName" type="text" class="nx-input" name="firstName"
                    [(ngModel)]="form.firstName" placeholder="e.g. Aarav" autocomplete="given-name" />
                </div>
                <div class="nx-field nx-f-mid">
                  <label class="nx-label" for="inp-middleName">
                    {{ isStudentAdmission ? 'Student middle name' : 'Middle name' }} <small>optional</small>
                  </label>
                  <input id="inp-middleName" type="text" class="nx-input" name="middleName"
                    [(ngModel)]="form.middleName" placeholder="e.g. Kumar" autocomplete="additional-name" />
                </div>
                <div class="nx-field nx-f-last">
                  <label class="nx-label" for="inp-lastName">
                    {{ isStudentAdmission ? 'Student last name' : 'Last name' }} <i class="req">*</i>
                  </label>
                  <input id="inp-lastName" type="text" class="nx-input" name="lastName"
                    [(ngModel)]="form.lastName" placeholder="e.g. Sharma" autocomplete="family-name" />
                </div>
              </div>

              <!-- Gender -->
              <div class="nx-field">
                <label class="nx-label">Gender <small>optional</small></label>
                <div class="nx-seg">
                  <button type="button" class="nx-seg-btn" *ngFor="let g of genders"
                    [class.on]="form.gender === g" (click)="form.gender = g">{{ g }}</button>
                </div>
              </div>

              <!-- Society · Flat member designation -->
              <div class="nx-field" *ngIf="isFlatMember">
                <label class="nx-label">Your designation <i class="req">*</i></label>
                <div class="nx-seg">
                  <button type="button" class="nx-seg-btn" *ngFor="let d of flatDesignations"
                    [class.on]="form.designationType === d" (click)="pickDesignation(d)">{{ d }}</button>
                </div>
                <div class="nx-desig-other" *ngIf="form.designationType === 'Other'" @expand>
                  <input id="inp-designationOther" type="text" class="nx-input" name="designationOther"
                    [(ngModel)]="form.designationOther" maxlength="40" placeholder="e.g. Flat Member" />
                </div>
              </div>

              <!-- Parent name (student) -->
              <div class="nx-row" *ngIf="isStudentAdmission">
                <div class="nx-field">
                  <label class="nx-label" for="inp-parentFirstName">Parent first name</label>
                  <input id="inp-parentFirstName" type="text" class="nx-input" name="parentFirstName"
                    [(ngModel)]="form.parentFirstName" placeholder="First name" />
                </div>
                <div class="nx-field">
                  <label class="nx-label" for="inp-parentLastName">Parent last name</label>
                  <input id="inp-parentLastName" type="text" class="nx-input" name="parentLastName"
                    [(ngModel)]="form.parentLastName" placeholder="Last name" />
                </div>
              </div>

              <!-- Mobile + OTP, all in one place -->
              <div class="nx-field">
                <label class="nx-label" for="inp-mobileNo">
                  {{ isStudentAdmission ? 'Parent mobile number' : 'Mobile number' }} <i class="req">*</i>
                </label>

                <div class="nx-inputrow" [class.verified]="otpVerified" [class.invalid]="mobileTouched && !isMobileValid">
                  <span class="nx-prefix">+91</span>
                  <input
                    #mobileInput
                    id="inp-mobileNo"
                    type="tel"
                    inputmode="numeric"
                    maxlength="10"
                    class="nx-input bare"
                    placeholder="10-digit mobile number"
                    autocomplete="tel-national"
                    [value]="mobileValue"
                    [readonly]="otpVerified"
                    (input)="onMobileInput($event)"
                    (blur)="mobileTouched = true"
                    (keydown.enter)="$event.preventDefault(); sendOtp()" />

                  <button
                    type="button"
                    class="nx-otpbtn"
                    id="btn-send-otp"
                    *ngIf="!otpVerified"
                    [class.ready]="isMobileValid && !otpSent"
                    [disabled]="!isMobileValid || isSendingOtp || otpTimer > 0"
                    (click)="sendOtp()">
                    <span *ngIf="isSendingOtp" class="nx-spin"></span>
                    <span *ngIf="!isSendingOtp">{{ !otpSent ? 'Get OTP' : (otpTimer > 0 ? 'Resend in ' + otpTimer + 's' : 'Resend OTP') }}</span>
                  </button>

                  <span class="nx-verified" *ngIf="otpVerified">
                    <i class="bi bi-patch-check-fill"></i> Verified
                    <button type="button" class="nx-change" (click)="resetOtp(); mobileInput.focus()">Change</button>
                  </span>
                </div>

                <small class="nx-err" *ngIf="mobileTouched && !isMobileValid && !otpVerified">
                  Enter a valid 10-digit mobile number starting with 6 to 9.
                </small>

                <!-- OTP boxes slide open under the field -->
                <div class="nx-otp" *ngIf="otpSent && !otpVerified" @expand>
                  <p class="nx-otp-msg">We've sent a 6-digit code to <strong>+91 {{ mobileValue }}</strong></p>
                  <div class="nx-otpboxes" [class.shake]="otpError" (paste)="onOtpPaste($event)">
                    <input
                      #otpBox
                      *ngFor="let i of otpIndexes"
                      class="nx-otpbox"
                      type="text"
                      inputmode="numeric"
                      maxlength="1"
                      [style.--i]="i"
                      [attr.autocomplete]="i === 0 ? 'one-time-code' : 'off'"
                      [attr.aria-label]="'OTP digit ' + (i + 1)"
                      [value]="otpDigits[i]"
                      [class.filled]="otpDigits[i]"
                      [class.bad]="otpError"
                      [readonly]="isVerifyingOtp"
                      (input)="onOtpInput($event, i)"
                      (keydown)="onOtpKeydown($event, i)"
                      (focus)="$any($event.target).select()" />
                  </div>
                  <div class="nx-otp-status" *ngIf="isVerifyingOtp"><span class="nx-spin dark"></span> Checking your code…</div>
                  <div class="nx-otp-status bad" *ngIf="otpError && !isVerifyingOtp"><i class="bi bi-x-circle-fill"></i> That code didn't match. Give it another go.</div>
                </div>
              </div>

              <!-- Optional extras -->
              <button type="button" class="nx-more" (click)="showMore = !showMore" [attr.aria-expanded]="showMore">
                <i class="bi" [ngClass]="showMore ? 'bi-dash-lg' : 'bi-plus-lg'"></i>
                {{ showMore ? 'Hide extra details' : 'Add more details' }} <small>optional</small>
              </button>

              <div class="nx-more-body" *ngIf="showMore" @expand>
                <div class="nx-more-inner">

                  <div class="nx-row">
                    <div class="nx-field" *ngIf="!isStudentAdmission">
                      <label class="nx-label" for="inp-emailId">Email</label>
                      <input id="inp-emailId" type="email" class="nx-input" name="emailId"
                        [(ngModel)]="form.emailId" placeholder="name@example.com" autocomplete="email" />
                    </div>
                    <div class="nx-field">
                      <label class="nx-label" for="inp-dob">Date of birth</label>
                      <input id="inp-dob" type="date" class="nx-input" name="dob" [(ngModel)]="form.dob" />
                    </div>
                  </div>

                  <!-- Hospital -->
                  <div class="nx-row" *ngIf="module === 'hospital'">
                    <div class="nx-field">
                      <label class="nx-label" for="inp-department">
                        Department
                        <span *ngIf="departmentsLoading" class="nx-spin dark ms-1"></span>
                      </label>
                      <select id="inp-department" class="nx-input" name="department" [(ngModel)]="form.department">
                        <option value="">Select Department</option>
                        <option *ngFor="let dept of departments" [value]="dept.departmentName || dept.name">
                          {{ dept.departmentName || dept.name }}
                        </option>
                      </select>
                    </div>
                    <div class="nx-field" *ngIf="selectedRole === 'Doctor'">
                      <label class="nx-label" for="inp-specialization">Specialization</label>
                      <input id="inp-specialization" type="text" class="nx-input" name="specialization"
                        [(ngModel)]="form.specialization" placeholder="e.g. Cardiologist" />
                    </div>
                  </div>

                  <!-- Address + PIN (not asked from flat members) -->
                  <div class="nx-row" *ngIf="!isFlatMember">
                    <div class="nx-field">
                      <label class="nx-label" for="inp-address">Address</label>
                      <input id="inp-address" type="text" class="nx-input" name="address"
                        [(ngModel)]="form.address" placeholder="Street / Area" autocomplete="street-address" />
                    </div>
                    <div class="nx-field">
                      <label class="nx-label" for="inp-pinCode">PIN code</label>
                      <input id="inp-pinCode" type="text" inputmode="numeric" maxlength="6" class="nx-input" name="pinCode"
                        [(ngModel)]="form.pinCode" placeholder="6-digit PIN" autocomplete="postal-code" />
                    </div>
                  </div>

                </div>
              </div>

              <!-- Submit -->
              <div class="nx-actions">
                <button
                  type="submit"
                  class="nx-btn"
                  id="btn-submit-onboarding"
                  [class.ready]="canSubmit && !isSubmitting"
                  [disabled]="!canSubmit || isSubmitting">
                  <span *ngIf="!isSubmitting" class="nx-btn-label">Submit my request <i class="bi bi-arrow-right"></i></span>
                  <span *ngIf="isSubmitting" class="nx-btn-loading"><span class="nx-spin"></span> Submitting your request…</span>
                </button>
                <p class="nx-hint" *ngIf="submitHint && !isSubmitting">
                  <i class="bi bi-info-circle"></i> {{ submitHint }}
                </p>
              </div>

            </form>
          </section>

          <!-- ═══ STEP 3 · SUCCESS + APP DOWNLOAD ═══ -->
          <section *ngIf="currentStep === 'success'" class="nx-step nx-success" @stepAnim>

            <div class="nx-check-wrap">
              <div class="nx-burst" aria-hidden="true">
                <span *ngFor="let p of confetti"
                  [style.--x]="p.x + 'px'" [style.--y]="p.y + 'px'" [style.--r]="p.r + 'deg'"
                  [style.--c]="p.c" [style.--d]="p.d + 'ms'"></span>
              </div>
              <span class="nx-ripple" aria-hidden="true"></span>
              <div class="nx-check">
                <svg viewBox="0 0 52 52" aria-hidden="true"><path d="M14 27l8 8 16-17" /></svg>
              </div>
            </div>

            <h2 class="nx-title center">You're all set, {{ form.firstName }}! 🎉</h2>
            <p class="nx-sub center">
              Your request is on its way to {{ entityName }}. The moment it's approved, you can log in and get started.
            </p>

            <!-- App download: first thing after the success message -->
            <div class="nx-app" [class.two-qr]="qrCodes.length > 1">
              <div class="nx-app-text">
                <h3>Download the Nishwik app</h3>
                <p>Install the app now, so you can log in the instant you're approved.</p>
                <div class="nx-stores">
                  <a class="nx-store" *ngIf="platform !== 'ios'" [href]="appLinks.playStore"
                     target="_blank" rel="noopener" id="lnk-play-store" (click)="cancelRedirect()">
                    <i class="bi bi-google-play"></i>
                    <span><small>Get it on</small>Google Play</span>
                  </a>
                  <a class="nx-store" *ngIf="platform !== 'android'" [href]="appLinks.appStore"
                     target="_blank" rel="noopener" id="lnk-app-store" (click)="cancelRedirect()">
                    <i class="bi bi-apple"></i>
                    <span><small>Download on the</small>App Store</span>
                  </a>
                </div>

                <!-- Mobile only: auto-open the right store -->
                <div class="nx-redirect" *ngIf="redirectIn > 0">
                  <span class="nx-spin"></span>
                  <span>Opening {{ storeName }} in {{ redirectIn }}s</span>
                  <button type="button" (click)="cancelRedirect()">Stay here</button>
                </div>
              </div>

              <!-- QR codes hold the real store link for the detected device (both on desktop) -->
              <div class="nx-qrs">
                <div class="nx-qr" *ngFor="let q of qrCodes">
                  <img *ngIf="!qrFailed[q.key]; else qrFallback" [src]="q.src"
                       [attr.width]="qrCodes.length > 1 ? 112 : 132" [attr.height]="qrCodes.length > 1 ? 112 : 132"
                       [alt]="'Scan to download the Nishwik app: ' + q.label" (error)="qrFailed[q.key] = true" />
                  <ng-template #qrFallback>
                    <a class="nx-qr-link" [href]="q.href" target="_blank" rel="noopener">Open download link</a>
                  </ng-template>
                  <span>{{ q.label }}</span>
                </div>
              </div>
            </div>

            <div class="nx-pills">
              <span class="nx-pill">{{ roleIcon }} {{ roleLabel }}</span>
              <span class="nx-pill pending"><i class="bi bi-hourglass-split"></i> Awaiting approval</span>
            </div>

            <ol class="nx-timeline">
              <li class="done">
                <span class="dot"><i class="bi bi-check-lg"></i></span>
                <div><strong>Request submitted</strong><small>Delivered to {{ entityName }}</small></div>
              </li>
              <li class="active">
                <span class="dot"><i class="bi bi-hourglass-split"></i></span>
                <div><strong>Under review</strong><small>Waiting for approval from {{ entityName }}</small></div>
              </li>
              <li>
                <span class="dot"><i class="bi bi-unlock-fill"></i></span>
                <div><strong>You're in</strong><small>Log in with the Nishwik app and start</small></div>
              </li>
            </ol>

          </section>

          <footer class="nx-trust">
            <i class="bi bi-lock-fill"></i> Your details are encrypted before they leave your device.
          </footer>

        </main>
      </div>
    </div>
  </div>
</div>